import { z } from 'zod'
import {
  localeSchema,
  moneySchema,
  optionalText,
  pageQuerySchema,
  quantitySchema,
  versionSchema,
} from './common.js'
import {
  type CustomPriceCell,
  levelPriceInputSchema,
  type PriceCell,
  type PriceLevel,
  packBarcodeInputSchema,
  productPackInputSchema,
  type SaleUnit,
} from './pricing.js'

// ── units & categories ─────────────────────────────────────────────────────────

const unitFields = z.object({
  name: z.string().trim().min(1).max(50),
  nameAr: optionalText(50),
  symbol: z.string().trim().min(1).max(10),
  symbolAr: optionalText(10),
  /** Whether quantities in this unit may have decimals (kg, litre) or must be whole (piece, box). */
  allowFractions: z.boolean().default(false),
  /**
   * A pack unit's usual content (ADR-045): "1 Carton = 12 Piece" is `packOfUnitId` Piece, `packFactor` 12.
   * Only a suggestion: a product adding this unit as a pack of that base unit starts with this factor and
   * may hold another number. Both or neither.
   */
  packOfUnitId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
  packFactor: productPackInputSchema.shape.factor.nullish().transform((v) => v ?? null),
})
const packOfBoth = (v: { packOfUnitId: string | null; packFactor: string | null }) =>
  (v.packOfUnitId === null) === (v.packFactor === null)
const packOfBothRule = { path: ['packFactor'], message: 'Give both the unit the pack holds and how many' }
export const unitInputSchema = unitFields.refine(packOfBoth, packOfBothRule)
export type UnitInput = z.input<typeof unitInputSchema>
export const unitUpdateSchema = unitFields
  .extend({ version: versionSchema })
  .refine(packOfBoth, packOfBothRule)

export interface Unit {
  id: string
  name: string
  nameAr: string | null
  symbol: string
  symbolAr: string | null
  allowFractions: boolean
  /** The usual content of this unit as a pack ("1 Carton = 12 Piece"), or null. Always sent; optional for older fixtures. */
  packOf?: { unitId: string; symbol: string; symbolAr: string | null; factor: string } | null
  /** Active products stocked in this unit, and active products selling it as a pack. */
  usage?: { baseOf: number; packOf: number }
  archived: boolean
  version: number
}

export const productCategoryInputSchema = z.object({
  name: z.string().trim().min(1).max(100),
  nameAr: optionalText(100),
  parentId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
})
export type ProductCategoryInput = z.input<typeof productCategoryInputSchema>
export const productCategoryUpdateSchema = productCategoryInputSchema.extend({ version: versionSchema })

export interface ProductCategory {
  id: string
  name: string
  nameAr: string | null
  parentId: string | null
  /** Active products in it and in the categories under it (what the product list filtered by it shows). */
  productCount: number
  archived: boolean
  version: number
}

// ── attributes ─────────────────────────────────────────────────────────────────

export const productAttributeInputSchema = z.object({
  name: z.string().trim().min(1).max(50),
  nameAr: optionalText(50),
  values: z
    .array(
      z.object({
        id: z.uuid().optional(),
        value: z.string().trim().min(1).max(50),
        valueAr: optionalText(50),
      }),
    )
    .min(1, 'Add at least one value')
    .max(100),
})
export type ProductAttributeInput = z.input<typeof productAttributeInputSchema>

export interface ProductAttribute {
  id: string
  name: string
  nameAr: string | null
  values: { id: string; value: string; valueAr: string | null }[]
  /** An archived attribute is no longer offered for new variants (only one no variant uses can be). */
  archived: boolean
}

// ── products & variants ────────────────────────────────────────────────────────

export const productTypeSchema = z.enum(['GOODS', 'SERVICE'])
export type ProductType = z.infer<typeof productTypeSchema>

export const variantInputSchema = z.object({
  id: z.uuid().optional(),
  name: optionalText(100),
  nameAr: optionalText(100),
  sku: optionalText(50),
  barcode: optionalText(50),
  /** Retail selling price of the base unit, excluding tax (the list price). */
  price: moneySchema.default('0'),
  /**
   * The rest of the price grid (ADR-045): Wholesale and Distributor prices of the base unit, and any level's
   * price of a pack. A cell left out falls back (pricing.ts `resolvePrice`).
   */
  levelPrices: z.array(levelPriceInputSchema).max(40).default([]),
  /** A barcode per pack of this variant (scanning it puts the pack on the line). */
  packBarcodes: z.array(packBarcodeInputSchema).max(10).default([]),
  reorderLevel: quantitySchema.default('0'),
  isDefault: z.boolean().default(false),
  attributes: z
    .array(z.object({ attributeId: z.uuid(), valueId: z.uuid() }))
    .max(5)
    .default([]),
})
export type VariantInput = z.input<typeof variantInputSchema>

export const productInputSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(200),
  nameAr: optionalText(200),
  description: optionalText(2000),
  type: productTypeSchema,
  trackSerials: z.boolean().default(false),
  categoryId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
  unitId: z.uuid(),
  taxRateId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
  status: z.enum(['ACTIVE', 'INACTIVE']).default('ACTIVE'),
  /**
   * Packs the product is also sold and bought in (Carton = 12 of the base unit), each a different unit of
   * the units list. Not for serial-tracked products (serials are counted one by one).
   */
  packs: z.array(productPackInputSchema).max(10).default([]),
  /** One variant for a simple product; several (each with attribute values) for a product with variants. */
  variants: z.array(variantInputSchema).min(1, 'A product needs at least one variant').max(200),
})
export type ProductInput = z.input<typeof productInputSchema>
export const productUpdateSchema = productInputSchema.extend({ version: versionSchema })
export type ProductUpdate = z.input<typeof productUpdateSchema>

/**
 * Stock views of the product list (need `inventory.read`, else 403): `low` — goods with at least one
 * active variant at or below its reorder level (reorder level above zero); `out` — goods whose active
 * variants hold nothing (on hand <= 0). Both read the chosen warehouse, or all warehouses. Services are
 * never in either.
 */
export const productStockFilterSchema = z.enum(['low', 'out'])
export type ProductStockFilter = z.infer<typeof productStockFilterSchema>

/**
 * `sort`: name, createdAt, price (lowest variant price), onHand (needs `inventory.read`).
 * `warehouseId` narrows `onHand` and the stock views to one warehouse.
 */
export const productListQuerySchema = pageQuerySchema.extend({
  view: z.enum(['current', 'active', 'inactive', 'archived']).default('current'),
  type: productTypeSchema.optional(),
  categoryId: z.uuid().optional(),
  warehouseId: z.uuid().optional(),
  stock: productStockFilterSchema.optional(),
})
export type ProductListQuery = z.input<typeof productListQuerySchema>

/** `GET /products/summary`: the list's filters without the view, stock view, paging or sort. */
export const productSummaryQuerySchema = productListQuerySchema.pick({
  q: true,
  type: true,
  categoryId: true,
  warehouseId: true,
})
export type ProductSummaryQuery = z.input<typeof productSummaryQuerySchema>

/**
 * Counts of the product list's view tabs for the given filters; each equals the `total` of the list it
 * opens. `lowStock` and `outOfStock` count the current (unarchived) products of the `stock` views, and
 * are null without `inventory.read`.
 */
export interface ProductListSummary {
  current: { count: number }
  active: { count: number }
  inactive: { count: number }
  archived: { count: number }
  lowStock: { count: number } | null
  outOfStock: { count: number } | null
}

/** The most rows one product export returns; a larger filtered set is refused (422 `EXPORT_TOO_LARGE`). */
export const PRODUCT_EXPORT_MAX_ROWS = 50_000

/**
 * `GET /products/export` (`products.export`): the list's filters, view and sort, every matching row, as
 * CSV (UTF-8 with a BOM). The On hand column is present only for `inventory.read`; never any cost.
 */
export const productExportQuerySchema = productListQuerySchema
  .omit({ page: true, pageSize: true })
  .extend({ format: z.enum(['csv']).default('csv'), lang: localeSchema.default('en') })
export type ProductExportQuery = z.input<typeof productExportQuerySchema>

export interface ProductListItem {
  id: string
  name: string
  nameAr: string | null
  type: ProductType
  category: { id: string; name: string; nameAr: string | null } | null
  unit: { id: string; symbol: string; symbolAr: string | null }
  sku: string | null
  barcode: string | null
  variantCount: number
  priceMin: string
  priceMax: string
  status: 'ACTIVE' | 'INACTIVE'
  archived: boolean
  /**
   * On hand over the active variants, in `warehouseId` or all warehouses. Null for services and for
   * callers without `inventory.read`. Always sent by the API; optional in the type only until the web fixtures carry it.
   */
  onHand?: string | null
  /** Some active variant is at or below its reorder level (null as `onHand`). */
  lowStock?: boolean | null
}

export interface ProductVariant {
  id: string
  name: string | null
  nameAr: string | null
  sku: string | null
  barcode: string | null
  price: string
  /** The grid's other cells; `unitId` null is the base unit. Always sent by the API; optional for older fixtures. */
  levelPrices?: { unitId: string | null; level: PriceLevel; price: string }[]
  /** Always sent by the API; optional for older fixtures. */
  packBarcodes?: { unitId: string; barcode: string }[]
  reorderLevel: string
  isDefault: boolean
  archived: boolean
  attributes: {
    attributeId: string
    attributeName: string
    valueId: string
    value: string
    valueAr: string | null
  }[]
}

export interface Product {
  id: string
  name: string
  nameAr: string | null
  description: string | null
  type: ProductType
  trackSerials: boolean
  category: { id: string; name: string; nameAr: string | null } | null
  /** `nameAr` and `symbolAr` are always sent by the API; optional for older fixtures. */
  unit: {
    id: string
    name: string
    nameAr?: string | null
    symbol: string
    symbolAr?: string | null
    allowFractions: boolean
  }
  taxRate: { id: string; name: string; rate: string } | null
  /** Packs in the order entered. Always sent by the API; optional for older fixtures. */
  packs?: {
    unitId: string
    name: string
    nameAr: string | null
    symbol: string
    symbolAr: string | null
    factor: string
    allowFractions: boolean
  }[]
  hasVariants: boolean
  status: 'ACTIVE' | 'INACTIVE'
  archivedAt: string | null
  variants: ProductVariant[]
  version: number
  createdAt: string
  updatedAt: string
}

/**
 * 409 `SKU_TAKEN` / `BARCODE_TAKEN`: the product that already holds the code, in the problem's `holder`
 * member. An archived holder can be restored, or its codes released (`POST /products/:id/release-codes`).
 */
export interface ProductCodeHolder {
  productId: string
  /** The SKU or barcode that clashed. */
  code: string
  name: string
  nameAr: string | null
  archived: boolean
}

export const PRODUCT_CODE_ERROR_CODES = ['SKU_TAKEN', 'BARCODE_TAKEN'] as const
export type ProductCodeErrorCode = (typeof PRODUCT_CODE_ERROR_CODES)[number]

/** Picker/scanner lookup for document lines and the POS: one row per sellable variant. */
export const variantLookupQuerySchema = z.object({
  q: z.string().trim().min(1).max(100),
  limit: z.coerce.number().int().min(1).max(50).default(20),
  /** The document's customer: its custom prices come with each row (ADR-045). */
  customerId: z.uuid().optional(),
  /** The document's date, for dated custom prices (default today). */
  date: z.iso.date().optional(),
})

export interface VariantLookup {
  variantId: string
  productId: string
  label: string
  labelAr: string | null
  sku: string | null
  barcode: string | null
  price: string
  type: ProductType
  unitSymbol: string
  allowFractions: boolean
  taxRateId: string | null
  /** True when the query matched this variant's barcode or SKU exactly. */
  exact: boolean
  /** The product tracks serial numbers: its lines carry one per unit. Always sent by the API; optional in the type only until the web fixtures carry it. */
  trackSerials?: boolean
  /** Units it is sold in, base first (ADR-045). Always sent by the API; optional for older fixtures. */
  units?: SaleUnit[]
  /** The price grid's explicit cells, the base Retail price included. Always sent; optional for older fixtures. */
  prices?: PriceCell[]
  /** With `customerId`: that customer's custom prices on the date. */
  customPrices?: CustomPriceCell[]
  /** The query was this pack's barcode: the line starts in that unit. */
  scannedUnitId?: string | null
}

/**
 * `GET /product-variants/:id`: one variant as the lookup shows it, for a form opened on a known
 * variant (Stock levels' Adjust, Count and Transfer). Open to every lookup permission, so it answers
 * without `products.read` and while the Catalog module is off. Archived variants are returned too;
 * `archived` is true when the variant or its product is archived.
 */
export interface VariantRef extends Omit<VariantLookup, 'exact' | 'trackSerials'> {
  trackSerials: boolean
  archived: boolean
}

/**
 * `GET /product-variants/purchase-lookup`: the purchasing line picker. The plain lookup never carries
 * cost (cashiers use it); this one adds the figures a buyer prices a purchase line with.
 */
export interface PurchaseVariantLookup extends VariantLookup {
  /** Ex-VAT unit cost after discount on the latest posted purchase of this variant; null if never bought. */
  lastPurchaseCost: string | null
  /** Purchase date of that purchase. */
  lastPurchaseDate: string | null
  /** Current weighted-average cost (all warehouses); null for services and never-stocked goods. */
  averageCost: string | null
  // `price` (from VariantLookup) is the current selling price: the base unit's Retail price, ex-VAT, the
  // price the till and invoices start from. A buyer sees it, with the margin, against the line's cost.
}

// ── product 360 ────────────────────────────────────────────────────────────────

export interface ProductWarehouseRef {
  id: string
  code: string
  name: string
  nameAr: string | null
}

/**
 * NEGATIVE: some variant is below zero in some warehouse; OUT: nothing on hand overall; LOW: some
 * active variant at or below its reorder level; OK otherwise.
 */
export type ReorderStatus = 'OK' | 'LOW' | 'OUT' | 'NEGATIVE'

export interface ProductStockSummary {
  /** Over the active variants and all warehouses. */
  onHand: string
  /** Each variant's on hand x its weighted-average cost, summed (3 dp). */
  value: string
  /** value / onHand (6 dp) while stock is held, else null. */
  averageCost: string | null
  reorderStatus: ReorderStatus
  /** Per warehouse where an active variant has a balance row, the main warehouse first. */
  warehouses: { warehouse: ProductWarehouseRef; onHand: string; value: string }[]
  /** Every variant, archived ones last (they count only while they still hold stock). */
  variants: {
    variantId: string
    label: string | null
    sku: string | null
    archived: boolean
    onHand: string
    reorderLevel: string
    averageCost: string
    value: string
    lowStock: boolean
    /** Only warehouses where this variant has a balance row. */
    warehouses: { warehouseId: string; onHand: string }[]
  }[]
}

/** The document a history line belongs to: the web links `type` + `id` to its detail page. */
export type ProductDocumentType = 'INVOICE' | 'DEBIT_NOTE' | 'CREDIT_NOTE' | 'PURCHASE' | 'SUPPLIER_RETURN'

export interface ProductDocumentRef {
  type: ProductDocumentType
  id: string
  number: string | null
}

/** The customer (sales) or supplier (purchases) of a document. */
export interface ProductPartyRef {
  id: string
  code: string
  name: string
  nameAr: string | null
}

export interface ProductSalesSummary {
  /** The fiscal year to date: first day of the current fiscal year to today. */
  from: string
  to: string
  quantitySold: string
  quantityReturned: string
  netQuantity: string
  /** Posted invoices and debit notes less posted credit notes, excluding VAT. */
  netSales: string
  /** The latest posted invoice (or debit note) line of the product, at any date. */
  lastSale: { date: string; document: ProductDocumentRef; party: ProductPartyRef } | null
}

export interface ProductPurchaseSummary {
  /** The latest posted purchase line of the product, at any date. */
  lastPurchase: {
    date: string
    document: ProductDocumentRef
    /** Null for a reader without `suppliers.read` (G26), with `partyHidden: true`. */
    party: ProductPartyRef | null
    partyHidden?: boolean
    /** Ex-VAT unit cost after discount on that purchase line (6 dp). */
    unitCost: string
  } | null
}

/**
 * `GET /products/:id/summary` (`products.read`): the figures of the product 360. Each section is null
 * when the caller lacks its permission: `stock` needs `inventory.read` (it carries cost and value, Q10)
 * and is also null for services; `sales` needs `sales.read` (never cost); `purchases` needs
 * `purchasing.read`.
 */
export interface ProductSummary {
  productId: string
  type: ProductType
  asOf: string
  stock: ProductStockSummary | null
  sales: ProductSalesSummary | null
  purchases: ProductPurchaseSummary | null
}

/** `GET /products/:id/sales` and `/products/:id/purchases`: paged, newest first. */
export const productHistoryQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  variantId: z.uuid().optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
})
export type ProductHistoryQuery = z.input<typeof productHistoryQuerySchema>

/**
 * One posted document line of the product. Quantities and amounts are signed: a return (credit note or
 * supplier return of kind RETURN) is negative; an allowance moves no goods (quantity 0) and only
 * reduces the amount. Amounts exclude VAT. Sales lines never carry cost.
 */
export interface ProductHistoryLine {
  id: string
  date: string
  document: ProductDocumentRef
  /**
   * The customer or supplier. Null on purchase history for a reader without `suppliers.read` (G26),
   * with `partyHidden: true`.
   */
  party: ProductPartyRef | null
  partyHidden?: boolean
  variantId: string
  variantLabel: string | null
  quantity: string
  /** Ex-VAT price (sales) or cost (purchases) per unit after discount; null when the quantity is 0. */
  unitAmount: string | null
  net: string
}
